import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { fanzaApi } from '../../api/fanza.js';
import { ApiError } from '../../api/client.js';
import type { FzPrize, FzPrizeInput, FzProduct } from '../../api/types.js';
import { Spinner, ErrorBox, EmptyBox, Pagination, Field, SafeLink } from '../../components/ui.js';
import { LotteryGradeBadge } from '../../components/LotteryGradeBadge.js';
import { text, money, num, datetime, dateOnly } from '../../lib/format.js';
import { checkImageUrl, checkLen, checkPercent, hasFieldErrors, inputCls, type FieldErrors } from '../../lib/formValidation.js';

const GRADES = ['A', 'B', 'C', 'D', 'E', 'L'];

/**
 * 원본: fanza/product/prize_list.php + prize_modify.php + prize_add.php + prize_copy.php + prize_history.php
 *
 * 재고 변경은 서버에서 이력 INSERT 와 재고 UPDATE 를 한 트랜잭션으로 처리한다.
 * 둘 다 커밋되거나 둘 다 안 되거나이므로, "재고는 바뀌었는데 이력이 없는" 상태가
 * 생기지 않는다. 동시 수정은 409 로 막힌다.
 */
export function FzPrizesPage() {
  const { randomboxId: raw = '' } = useParams();
  const randomboxId = Number(raw);
  const queryClient = useQueryClient();

  const [historyFor, setHistoryFor] = useState<number | null>(null);
  const [formModal, setFormModal] = useState<{ mode: 'edit' | 'copy'; prize: FzPrize } | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<number | null>(null);

  const product = useQuery({
    queryKey: ['fanza', 'product', randomboxId],
    queryFn: () => fanzaApi.product(randomboxId),
    enabled: Number.isFinite(randomboxId) && randomboxId > 0,
  });

  const prizes = useQuery({
    queryKey: ['fanza', 'prizes', randomboxId],
    queryFn: () => fanzaApi.prizes(randomboxId),
    enabled: Number.isFinite(randomboxId) && randomboxId > 0,
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['fanza', 'prizes', randomboxId] });
  }

  const remove = useMutation({
    mutationFn: (prizeId: number) => fanzaApi.deletePrize(randomboxId, prizeId),
    onSuccess: () => {
      setConfirmingDelete(null);
      refresh();
    },
  });

  if (!Number.isFinite(randomboxId) || randomboxId <= 0) {
    return <ErrorBox error={new Error('잘못된 오나홀 상품 ID 입니다.')} />;
  }

  const prizeItems = prizes.data?.items ?? [];

  return (
    <section className="lottery-prize-page fanza-prize-page">
      <header className="page-head lottery-prize-page__head">
        <div>
          <Link className="link link--back" to="/fanza/products">
            ← 오나홀 목록
          </Link>
          <p className="lottery-prize-page__eyebrow">FANZA PRODUCT DETAIL</p>
          <h1 className="page-title">FANZA 오나홀 상세페이지</h1>
          <p className="lottery-prize-page__description">상품 정보와 세부 경품 구성·재고를 관리합니다.</p>
        </div>
        <span className="lottery-prize-page__id">#{randomboxId}</span>
      </header>

      {product.data && <FanzaProductSummary product={product.data} randomboxId={randomboxId} />}

      <div className="lottery-prize-section">
        <div className="lottery-prize-section__head">
          <div>
            <p className="lottery-prize-section__eyebrow">PRIZE INVENTORY</p>
            <h2 className="lottery-prize-section__title">
              세부 랜덤박스{product.data?.name ? ` (${product.data.name})` : ''}
            </h2>
          </div>
          {prizes.data && (
            <div className="lottery-prize-stats" aria-label="경품 요약">
              <span><b>{num(prizeItems.length)}</b>개 경품</span>
              <span><b>{num(prizes.data.totalQuantity)}</b>개 재고</span>
              <span><b>{num(prizes.data.totalDraw)}</b>회 추첨</span>
              <span className={prizes.data.soldoutCount > 0 ? 'lottery-prize-stats__warn' : undefined}>
                <b>{num(prizes.data.soldoutCount)}</b>개 품절
              </span>
            </div>
          )}
        </div>

        {prizes.isPending ? (
          <Spinner />
        ) : prizes.isError ? (
          <ErrorBox error={prizes.error} onRetry={() => void prizes.refetch()} />
        ) : prizeItems.length === 0 ? (
          <EmptyBox>등록된 경품이 없습니다.</EmptyBox>
        ) : (
          <div className="table-wrap lottery-prize-table-wrap">
            <table className="table lottery-prize-table fanza-prize-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'right' }}>No.</th>
                  <th>등급</th>
                  <th>이미지</th>
                  <th>상품 코드</th>
                  <th>설명</th>
                  <th style={{ textAlign: 'right' }}>수량</th>
                  <th style={{ textAlign: 'right' }}>추첨</th>
                  <th>품절</th>
                  <th>등록일</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {prizeItems.map((prize, i) => (
                  <PrizeRow
                    key={prize.prizeId ?? i}
                    index={i + 1}
                    randomboxId={randomboxId}
                    prize={prize}
                    confirmingDelete={confirmingDelete === prize.prizeId}
                    onShowHistory={() => setHistoryFor(prize.prizeId ?? null)}
                    onEdit={() => setFormModal({ mode: 'edit', prize })}
                    onCopy={() => setFormModal({ mode: 'copy', prize })}
                    onAskDelete={() => setConfirmingDelete(prize.prizeId)}
                    onCancelDelete={() => setConfirmingDelete(null)}
                    onConfirmDelete={() => {
                      if (prize.prizeId !== null) remove.mutate(prize.prizeId);
                    }}
                    deletePending={remove.isPending}
                    onSaved={() => {
                      refresh();
                      if (prize.prizeId !== null) {
                        void queryClient.invalidateQueries({
                          queryKey: ['fanza', 'prizeHistory', randomboxId, prize.prizeId],
                        });
                      }
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {historyFor !== null && (
        <HistoryPanel randomboxId={randomboxId} prizeId={historyFor} onClose={() => setHistoryFor(null)} />
      )}

      {formModal && (
        <PrizeFormModal
          mode={formModal.mode}
          randomboxId={randomboxId}
          prize={formModal.prize}
          onClose={() => setFormModal(null)}
          onSaved={() => {
            setFormModal(null);
            refresh();
          }}
        />
      )}
    </section>
  );
}

function FanzaProductSummary({ product, randomboxId }: { product: FzProduct; randomboxId: number }) {
  return (
    <article className="lottery-product-summary">
      <div className="lottery-product-summary__media">
        {product.thumbnail ? (
          <SafeLink href={product.thumbnail} title="원본 이미지 열기">
            <img src={product.thumbnail} alt="" />
          </SafeLink>
        ) : (
          <span>NO IMAGE</span>
        )}
      </div>

      <div className="lottery-product-summary__body">
        <div className="lottery-product-summary__title-row">
          <div>
            <span className="lottery-product-summary__label">랜덤박스 번호 {randomboxId}</span>
            <h2>{text(product.name ?? product.jpName)}</h2>
            {product.jpName && product.name !== product.jpName && <p>{text(product.jpName)}</p>}
          </div>
          <strong className="lottery-product-summary__price">{money(product.price)}</strong>
        </div>

        <dl className="lottery-product-summary__facts">
          <div>
            <dt>판매 기간</dt>
            <dd>{dateOnly(product.saleStart)} ~ {dateOnly(product.saleEnd)}</dd>
          </div>
          <div>
            <dt>발송 예정일</dt>
            <dd>{dateOnly(product.deliveryDate)}</dd>
          </div>
          <div>
            <dt>카테고리</dt>
            <dd>{text(product.categoryName)}</dd>
          </div>
          <div>
            <dt>등록일</dt>
            <dd>{dateOnly(product.regdate)}</dd>
          </div>
        </dl>

        {product.publicUrl && (
          <a className="lottery-product-summary__public-link" href={product.publicUrl} target="_blank" rel="noreferrer">
            공개 상품 페이지 열기 ↗
          </a>
        )}
      </div>

      <div className="lottery-product-summary__memo">
        <span>관리용 메모</span>
        <p>{text(product.memo)}</p>
      </div>
    </article>
  );
}

function PrizeRow({
  index,
  randomboxId,
  prize,
  confirmingDelete,
  onShowHistory,
  onEdit,
  onCopy,
  onAskDelete,
  onCancelDelete,
  onConfirmDelete,
  deletePending,
  onSaved,
}: {
  index: number;
  randomboxId: number;
  prize: FzPrize;
  confirmingDelete: boolean;
  onShowHistory: () => void;
  onEdit: () => void;
  onCopy: () => void;
  onAskDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  deletePending: boolean;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const current = prize.quantity ?? 0;
  const editing = draft !== null;

  const mutation = useMutation({
    mutationFn: (quantity: number) => {
      if (prize.prizeId === null) throw new Error('경품 ID 가 없습니다.');
      return fanzaApi.updatePrizeQuantity(randomboxId, prize.prizeId, quantity, reason || undefined);
    },
    onSuccess: () => {
      setDraft(null);
      setReason('');
      onSaved();
    },
  });

  const conflict = mutation.error instanceof ApiError && mutation.error.status === 409;
  const soldout = prize.soldoutFlag === 'Y';

  return (
    <tr className={soldout ? 'fanza-prize-table__soldout-row' : prize.grade === 'L' ? 'lottery-prize-table__lucky-row' : undefined}>
      <td className="td-right td-muted">{index}</td>
      <td><LotteryGradeBadge grade={prize.grade} /></td>
      <td className="lottery-prize-table__image-cell">
        {prize.thumbnail ? (
          <SafeLink href={prize.thumbnail} title="원본 이미지 열기">
            <img className="lottery-prize-table__image" src={prize.thumbnail} alt="" loading="lazy" />
          </SafeLink>
        ) : (
          <span className="lottery-prize-table__image-empty">NO IMAGE</span>
        )}
      </td>
      <td><code className="code lottery-prize-table__code">{text(prize.prizeCode)}</code></td>
      <td className="lottery-prize-table__description">
        <strong>{text(prize.description)}</strong>
        {prize.grade === 'L' && (
          <span className="lottery-prize-table__lucky-meta">
            {text(prize.luckyTitle)} · {dateOnly(prize.luckyStartDate)} ~ {dateOnly(prize.luckyEndDate)} · 확률{' '}
            {num(prize.drawProbability)}%
          </span>
        )}
      </td>
      <td className="td-right lottery-prize-table__quantity">
        <div className="qty">
          <input
            className="input input--qty"
            type="number"
            min={0}
            value={editing ? draft : String(current)}
            onChange={(e) => setDraft(e.target.value)}
            disabled={mutation.isPending || prize.prizeId === null}
            aria-label={`${text(prize.prizeCode)} 재고`}
          />
          {editing && (
            <>
              <input
                className="input input--reason"
                placeholder="사유 (예: 입고)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={60}
                aria-label="변경 사유"
              />
              <button
                type="button"
                className="btn btn--primary btn--sm"
                disabled={mutation.isPending}
                onClick={() => {
                  const value = Number(draft);
                  if (!Number.isFinite(value) || value < 0) return;
                  mutation.mutate(value);
                }}
              >
                {mutation.isPending ? '…' : '저장'}
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => {
                  setDraft(null);
                  setReason('');
                  mutation.reset();
                }}
              >
                취소
              </button>
            </>
          )}
        </div>
        {mutation.isError && (
          <div className={conflict ? 'qty-msg qty-msg--conflict' : 'qty-msg qty-msg--error'}>
            {mutation.error instanceof Error ? mutation.error.message : '저장 실패'}
            {conflict && (
              <button
                type="button"
                className="link"
                onClick={() => {
                  setDraft(null);
                  mutation.reset();
                  onSaved();
                }}
              >
                새로고침
              </button>
            )}
          </div>
        )}
      </td>
      <td className="td-right fanza-prize-table__draw">{num(prize.draw)}</td>
      <td>{soldout ? <span className="badge badge--danger">품절</span> : <span className="td-muted">-</span>}</td>
      <td className="td-muted lottery-prize-table__date">{datetime(prize.regdate)}</td>
      <td className="lottery-prize-table__actions-cell">
        <div className="lottery-prize-actions">
          <button type="button" className="btn btn--sm lottery-prize-action lottery-prize-action--edit" onClick={onEdit}>
            <span aria-hidden="true">✎</span>
            수정
          </button>
          <button type="button" className="btn btn--sm lottery-prize-action lottery-prize-action--copy" onClick={onCopy}>
            <span aria-hidden="true">⧉</span>
            복사
          </button>
          {confirmingDelete ? (
            <>
              <button type="button" className="btn btn--sm lottery-prize-action lottery-prize-action--delete" disabled={deletePending} onClick={onConfirmDelete}>
                정말 삭제
              </button>
              <button type="button" className="btn btn--ghost btn--sm" onClick={onCancelDelete}>
                취소
              </button>
            </>
          ) : (
            <button type="button" className="btn btn--sm lottery-prize-action lottery-prize-action--delete" onClick={onAskDelete}>
              <span aria-hidden="true">×</span>
              삭제
            </button>
          )}
          <button type="button" className="btn btn--sm lottery-prize-action lottery-prize-action--history" onClick={onShowHistory}>
            <span aria-hidden="true">↺</span>
            변경이력
          </button>
        </div>
      </td>
    </tr>
  );
}

interface PrizeFormState {
  grade: string;
  prizeCode: string;
  quantity: string;
  drawProbability: string;
  thumbnail: string;
  displayImg: string;
  luckyTitle: string;
  luckyStartDate: string;
  luckyEndDate: string;
  description: string;
  soldout: boolean;
}

function toPrizeFormState(p: FzPrize): PrizeFormState {
  return {
    grade: p.grade && GRADES.includes(p.grade) ? p.grade : GRADES[0]!,
    prizeCode: p.prizeCode ?? '',
    quantity: p.quantity !== null && p.quantity !== undefined ? String(p.quantity) : '0',
    drawProbability:
      p.drawProbability !== null && p.drawProbability !== undefined ? String(p.drawProbability) : '',
    thumbnail: p.thumbnail ?? '',
    displayImg: p.displayImg ?? '',
    luckyTitle: p.luckyTitle ?? '',
    luckyStartDate: p.luckyStartDate ? p.luckyStartDate.slice(0, 10) : '',
    luckyEndDate: p.luckyEndDate ? p.luckyEndDate.slice(0, 10) : '',
    description: p.description ?? '',
    soldout: p.soldoutFlag === 'Y',
  };
}

function toPrizeInput(form: PrizeFormState): FzPrizeInput {
  return {
    grade: form.grade,
    prizeCode: form.prizeCode,
    quantity: form.soldout ? 0 : Number(form.quantity) || 0,
    drawProbability: form.drawProbability === '' ? null : Number(form.drawProbability),
    thumbnail: form.thumbnail,
    displayImg: form.displayImg,
    luckyTitle: form.luckyTitle,
    luckyStartDate: form.luckyStartDate,
    luckyEndDate: form.luckyEndDate,
    description: form.description,
    soldoutFlag: form.soldout ? 'Y' : 'N',
  };
}

/** 경품 수정/복사 모달. 원본 prize_modify.php · prize_add.php · prize_copy.php 를 합쳤다. */
function PrizeFormModal({
  mode,
  randomboxId,
  prize,
  onClose,
  onSaved,
}: {
  mode: 'edit' | 'copy';
  randomboxId: number;
  prize: FzPrize;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<PrizeFormState>(() => toPrizeFormState(prize));

  const [submitted, setSubmitted] = useState(false);
  /** 규칙은 서버 필드 정의(prize-crud.ts 의 buildSchema)와 같은 값이다. */
  const errors: FieldErrors = {
    prizeCode: checkLen(form.prizeCode, 64, '상품 코드'),
    thumbnail: checkImageUrl(form.thumbnail),
    displayImg: checkImageUrl(form.displayImg),
    luckyTitle: checkLen(form.luckyTitle, 255, '행운상 타이틀'),
    description: checkLen(form.description, 5000, '설명'),
    drawProbability: checkPercent(form.drawProbability),
  };
  const shown: FieldErrors = submitted ? errors : {};

  const save = useMutation({
    mutationFn: () => {
      const body = toPrizeInput(form);
      return mode === 'edit' && prize.prizeId !== null
        ? fanzaApi.updatePrize(randomboxId, prize.prizeId, body)
        : fanzaApi.createPrize(randomboxId, body);
    },
    onSuccess: onSaved,
  });

  const isLucky = form.grade === 'L';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel modal-panel--wide" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <h2 className="modal-title">{mode === 'edit' ? '경품 수정' : '경품 복사'}</h2>

        {mode === 'copy' && (
          <p className="hint">
            <b>{text(prize.description)}</b>의 내용을 그대로 새 경품으로 등록합니다. 필요하면 아래
            값을 고친 뒤 저장하세요.
          </p>
        )}

        <form
          className="form-grid form-grid--stack"
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(true);
            if (hasFieldErrors(errors)) return;
            save.mutate();
          }}
        >
          <div className="row-3 prize-form__row--first">
            <Field label="등급">
              <select
                className="input"
                value={form.grade}
                onChange={(e) => setForm((f) => ({ ...f, grade: e.target.value }))}
              >
                {GRADES.map((g) => (
                  <option key={g} value={g}>
                    {g === 'L' ? '행운상' : g}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="상품 코드" error={shown.prizeCode}>
              <input
                className={inputCls(shown.prizeCode)}
                value={form.prizeCode}
                maxLength={64}
                onChange={(e) => setForm((f) => ({ ...f, prizeCode: e.target.value }))}
              />
            </Field>
            <Field label="수량 (개)">
              <div className="qty-input-row">
                <input
                  className="input"
                  type="number"
                  min={0}
                  value={form.quantity}
                  disabled={form.soldout}
                  onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
                />
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={form.soldout}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, soldout: e.target.checked, quantity: e.target.checked ? '0' : f.quantity }))
                    }
                  />
                  품절
                </label>
              </div>
            </Field>
          </div>

          {isLucky && (
            <div className="row-3">
              <Field label="확률 (%)" error={shown.drawProbability}>
                <input
                  className={inputCls(shown.drawProbability)}
                  type="number"
                  min={0}
                  value={form.drawProbability}
                  onChange={(e) => setForm((f) => ({ ...f, drawProbability: e.target.value }))}
                />
              </Field>
              <Field label="행운상 시작일">
                <input
                  className="input"
                  type="date"
                  value={form.luckyStartDate}
                  onChange={(e) => setForm((f) => ({ ...f, luckyStartDate: e.target.value }))}
                />
              </Field>
              <Field label="행운상 종료일">
                <input
                  className="input"
                  type="date"
                  value={form.luckyEndDate}
                  onChange={(e) => setForm((f) => ({ ...f, luckyEndDate: e.target.value }))}
                />
              </Field>
            </div>
          )}

          {isLucky && (
            <Field label="행운상 타이틀" error={shown.luckyTitle} full>
              <input
                className={inputCls(shown.luckyTitle)}
                value={form.luckyTitle}
                maxLength={255}
                onChange={(e) => setForm((f) => ({ ...f, luckyTitle: e.target.value }))}
              />
            </Field>
          )}

          <div className="prize-form__row--third">
            <Field label="당첨용 이미지 URL" error={shown.thumbnail} full>
              <div className="img-input-row">
                <input
                  className={inputCls(shown.thumbnail)}
                  value={form.thumbnail}
                maxLength={255}
                  placeholder="https://..."
                  onChange={(e) => setForm((f) => ({ ...f, thumbnail: e.target.value }))}
                />
                {form.thumbnail && <img className="thumb thumb--lg" src={form.thumbnail} alt="" />}
              </div>
            </Field>
          </div>

          <Field label="표시용 이미지 URL" error={shown.displayImg} full>
            <div className="img-input-row">
              <input
                className={inputCls(shown.displayImg)}
                value={form.displayImg}
                maxLength={255}
                placeholder="https://..."
                onChange={(e) => setForm((f) => ({ ...f, displayImg: e.target.value }))}
              />
              {form.displayImg && <img className="thumb thumb--lg" src={form.displayImg} alt="" />}
            </div>
          </Field>

          <Field label="설명" error={shown.description} full>
            <textarea
              className={inputCls(shown.description)}
              rows={4}
              value={form.description}
                maxLength={5000}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </Field>

          {save.isError && (
            <div className="qty-msg qty-msg--error">
              {save.error instanceof ApiError ? save.error.message : '저장에 실패했습니다.'}
            </div>
          )}

          <button type="submit" className="btn btn--primary btn--block" disabled={save.isPending}>
            {save.isPending ? '저장 중…' : mode === 'edit' ? '수정하기' : '복사본 만들기'}
          </button>
        </form>
      </div>
    </div>
  );
}

function HistoryPanel({
  randomboxId,
  prizeId,
  onClose,
}: {
  randomboxId: number;
  prizeId: number;
  onClose: () => void;
}) {
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ['fanza', 'prizeHistory', randomboxId, prizeId, page],
    queryFn: () => fanzaApi.prizeHistory(randomboxId, prizeId, page),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel modal-panel--wide lottery-prize-history" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <p className="lottery-prize-section__eyebrow">STOCK HISTORY</p>
        <h2 className="modal-title">재고 변경 이력 · 경품 #{prizeId}</h2>

        {query.isPending ? (
          <Spinner />
        ) : query.isError ? (
          <ErrorBox error={query.error} />
        ) : query.data.items.length === 0 ? (
          <EmptyBox>기록된 변경 이력이 없습니다.</EmptyBox>
        ) : (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>일시</th>
                    <th>작업</th>
                    <th style={{ textAlign: 'right' }}>이전</th>
                    <th style={{ textAlign: 'right' }}>이후</th>
                    <th style={{ textAlign: 'right' }}>변화</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.items.map((h) => {
                    const delta = (h.newQuantity ?? 0) - (h.oldQuantity ?? 0);
                    return (
                      <tr key={h.logId ?? `${h.regdate}-${h.oldQuantity}`}>
                        <td className="td-muted">{datetime(h.regdate)}</td>
                        <td>{text(h.action)}</td>
                        <td className="td-right">{num(h.oldQuantity)}</td>
                        <td className="td-right">{num(h.newQuantity)}</td>
                        <td className={delta < 0 ? 'td-right delta delta--down' : 'td-right delta delta--up'}>
                          {delta > 0 ? `+${delta}` : delta}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              page={query.data.page}
              totalPages={query.data.totalPages}
              total={query.data.total}
              onChange={setPage}
            />
          </>
        )}
      </div>
    </div>
  );
}
